import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  BookOpen, Search, Plus, ThumbsUp, ThumbsDown, Eye, Tag,
  RefreshCw, Brain, Zap, Trash2, ChevronRight, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LeadCapture } from '../components/LeadCapture';

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Knowledge Base');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', tags: '', visibility: 'public' });
  const [aiTopic, setAiTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');
  const isAdmin = ['platform_admin', 'business_admin', 'technician'].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const [artRes, catRes] = await Promise.all([
          api.get('/api/kb/articles'),
          api.get('/api/kb/categories'),
        ]);
        setArticles(artRes.data.articles || []);
        setCategories(catRes.data.categories || []);
        if ((artRes.data.articles || []).length === 0 && user?.role === 'platform_admin') {
          await api.post('/api/kb/seed');
          const { data } = await api.get('/api/kb/articles');
          setArticles(data.articles || []);
        }
      } catch (e) { /* handled */ } finally { setLoading(false); }
    })();
  }, [user?.role]);

  const handleSearch = async () => {
    try {
      if (search) {
        const params = new URLSearchParams({ q: search, limit: '50' });
        if (sourceFilter !== 'all') params.set('source', sourceFilter);
        const { data } = await api.get(`/api/kb/unified-search?${params}`);
        setArticles(data.results || []);
      } else {
        const params = new URLSearchParams();
        if (catFilter) params.set('category', catFilter);
        const { data } = await api.get(`/api/kb/articles?${params}`);
        setArticles(data.articles || []);
      }
    } catch { toast.error('Search failed'); }
  };

  const handleCreate = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    try {
      const { data } = await api.post('/api/kb/articles', {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setArticles(prev => [data, ...prev]);
      setForm({ title: '', content: '', category: 'general', tags: '', visibility: 'public' });
      setShowCreate(false);
      toast.success('Article created');
    } catch { toast.error('Failed to create'); }
  };

  const handleVote = async (articleId, vote) => {
    try {
      await api.post(`/api/kb/articles/${articleId}/vote`, { vote });
      setArticles(prev => prev.map(a => {
        if (a.id === articleId) {
          return { ...a, upvotes: a.upvotes + (vote === 'up' ? 1 : 0), downvotes: a.downvotes + (vote === 'down' ? 1 : 0) };
        }
        return a;
      }));
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(prev => ({
          ...prev,
          upvotes: prev.upvotes + (vote === 'up' ? 1 : 0),
          downvotes: prev.downvotes + (vote === 'down' ? 1 : 0),
        }));
      }
    } catch (e) { /* handled */ }
  };

  const handleDelete = async (articleId) => {
    try {
      await api.delete(`/api/kb/articles/${articleId}`);
      setArticles(prev => prev.filter(a => a.id !== articleId));
      setSelectedArticle(null);
      toast.success('Article deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleAiGenerate = async () => {
    if (!aiTopic) { toast.error('Enter a topic'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/api/kb/ai/generate', { topic: aiTopic });
      if (data.title) {
        setForm({
          title: data.title,
          content: data.content || '',
          category: data.category || 'general',
          tags: (data.tags || []).join(', '),
          visibility: 'public',
        });
        setShowCreate(true);
        toast.success('Article generated — review and save');
      }
    } catch { toast.error('AI generation failed'); }
    finally { setGenerating(false); }
  };

  const handleViewArticle = async (article) => {
    try {
      const { data } = await api.get(`/api/kb/articles/${article.id}`);
      setSelectedArticle(data);
    } catch { setSelectedArticle(article); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="kb-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="max-w-5xl space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Knowledge Base</h1>
              <p className="text-sm text-gray-500 mt-1">Find solutions, guides, and best practices</p>
            </div>
            {isAdmin && (
              <Button onClick={() => { setShowCreate(!showCreate); setSelectedArticle(null); }} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="kb-new-article-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Article
              </Button>
            )}
          </div>

          {/* AI Generator */}
          {isAdmin && !selectedArticle && (
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-5 text-white" data-testid="kb-ai-generator">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-5 h-5 text-[#D4A843]" />
                <h3 className="text-sm font-bold uppercase tracking-wider">AI Article Generator</h3>
                <span className="text-[10px] text-gray-400">GPT-5.2</span>
              </div>
              <div className="flex gap-2">
                <Input value={aiTopic} onChange={e => setAiTopic(e.target.value)} placeholder="Topic... e.g., 'How to configure VPN split tunneling'" className="rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400" data-testid="kb-ai-topic" />
                <Button onClick={handleAiGenerate} disabled={generating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg shrink-0" data-testid="kb-ai-generate-btn">
                  <Zap className="w-3.5 h-3.5 mr-1" />{generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </div>
          )}

          {/* Search */}
          {!selectedArticle && (
            <div className="flex gap-3 items-end">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Unified search across all knowledge sources..." className="rounded-lg pl-10" data-testid="kb-search"
                  onKeyDown={e => e.key === 'Enter' && handleSearch()} />
              </div>
              <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); }} className="h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="kb-source-filter">
                <option value="all">All Sources</option>
                <option value="local">TechOps Only</option>
                <option value="zendesk">Zendesk Only</option>
              </select>
              <Select value={catFilter} onValueChange={v => setCatFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="rounded-lg w-40" data-testid="kb-cat-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} variant="outline" className="rounded-lg" data-testid="kb-search-btn"><Search className="w-4 h-4" /></Button>
            </div>
          )}

          {/* Create Form */}
          {showCreate && !selectedArticle && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 animate-fade-in" data-testid="kb-create-form">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">New Article</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-lg" data-testid="kb-title-input" /></div>
                <div className="flex gap-3">
                  <div className="flex-1"><Label className="text-xs">Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                      <SelectTrigger className="mt-1 rounded-lg" data-testid="kb-cat-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1"><Label className="text-xs">Visibility</Label>
                    <Select value={form.visibility} onValueChange={v => setForm({...form, visibility: v})}>
                      <SelectTrigger className="mt-1 rounded-lg" data-testid="kb-vis-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="internal">Internal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div><Label className="text-xs">Content (Markdown) *</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" rows={10} data-testid="kb-content-input" /></div>
              <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} className="mt-1 rounded-lg" placeholder="vpn, network, troubleshooting" data-testid="kb-tags-input" /></div>
              <Button onClick={handleCreate} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="kb-save-btn">Save Article</Button>
            </div>
          )}

          {/* Article Detail */}
          {selectedArticle && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="kb-article-detail">
              <button onClick={() => setSelectedArticle(null)} className="text-xs text-[#D4A843] flex items-center gap-1 mb-4 hover:underline" data-testid="kb-back-btn">
                <ArrowLeft className="w-3 h-3" /> Back to articles
              </button>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900">{selectedArticle.title}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs bg-[#D4A843]/10 text-[#D4A843] px-2 py-0.5 rounded font-medium capitalize">{selectedArticle.category}</span>
                    {selectedArticle.source && <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${selectedArticle.source === 'zendesk' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>{selectedArticle.source}</span>}
                    {selectedArticle.external_url && <a href={selectedArticle.external_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D4A843] hover:underline">View Original</a>}
                    <span className="text-xs text-gray-400">By {selectedArticle.author_name}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1"><Eye className="w-3 h-3" /> {selectedArticle.views}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleVote(selectedArticle.id, 'up')} className="flex items-center gap-1 text-xs text-gray-500 hover:text-emerald-600 transition-colors" data-testid="kb-upvote">
                    <ThumbsUp className="w-4 h-4" /> {selectedArticle.upvotes}
                  </button>
                  <button onClick={() => handleVote(selectedArticle.id, 'down')} className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors" data-testid="kb-downvote">
                    <ThumbsDown className="w-4 h-4" /> {selectedArticle.downvotes}
                  </button>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500" onClick={() => handleDelete(selectedArticle.id)} data-testid="kb-delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {selectedArticle.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {selectedArticle.tags.map((t, i) => (
                    <span key={`item-${i}`} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex items-center gap-1"><Tag className="w-2.5 h-2.5" />{t}</span>
                  ))}
                </div>
              )}
              <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap" data-testid="kb-article-content">
                {selectedArticle.content}
              </div>
            </div>
          )}

          {/* Article List */}
          {!selectedArticle && !showCreate && (
            <div className="space-y-3" data-testid="kb-article-list">
              {articles.map(a => (
                <button key={a.id} onClick={() => handleViewArticle(a)} className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-[#D4A843] transition-colors" data-testid={`kb-article-${a.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">{a.title}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded font-medium capitalize">{a.category}</span>
                        {a.source && <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${a.source === 'zendesk' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>{a.source}</span>}
                        <span className="text-[10px] text-gray-400">{a.author_name}</span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" /> {a.views}</span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><ThumbsUp className="w-2.5 h-2.5" /> {a.upvotes}</span>
                        {a.visibility === 'internal' && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Internal</span>}
                      </div>
                      {a.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">{a.tags.slice(0, 4).map((t, i) => (
                          <span key={`item-${i}`} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{t}</span>
                        ))}</div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                  </div>
                </button>
              ))}
              {articles.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No articles found. Create one or use AI to generate.</p>}
            </div>
          )}

          {/* Lead Capture */}
          <LeadCapture magnet="security_updates" page="kb" title="Subscribe to Security Updates" subtitle="Weekly cybersecurity tips, threat alerts, and IT best practices — delivered to your inbox." variant="inline" />
        </div>
      </main>
    </div>
  );
}
